import _ from 'lodash'
import axios from 'axios'
import moment from 'moment'
import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  ClassRecord,
  ClassRecordsState,
  Grade,
  StudentWithGrade,
  classRecordTaskType,
  Criterion,
  Rating,
  ClassRecordForRegister,
} from '@/store/lessons/classRecord/types';
import { periodType } from '@/store/common.types';

const actions: ActionTree<ClassRecordsState, RootState> = {
  getClassRecordByLessonId: (
    { commit, dispatch, getters },
    { lessonId }: { lessonId: string },
  ) => axios.get(`/lessons/${lessonId}/grade`)
    .then(async ({ data }) => {
      await Promise.all([
        ..._.compact([
          data.tutorId,
          data.substituteTutorId,
        ]).map((tutorId) => dispatch('getTutor', { tutorId })),
        dispatch('fetchMasterCampuses'),
        dispatch('fetchMasterSemesters'),
        dispatch('fetchStudents', {
          conditions: {
            studentIds: _.map(data.students, 'studentId'),
          },
        }),
      ])

      const classRecord: ClassRecord = {
        lessonId,
        fiscalYear: data.fiscalYear,
        semester: getters.findSemesterByCode(data.semesterCode),
        teacher: getters.findTutorById(data.tutorId),
        substituteTutor: getters.findTutorById(data.substituteTutorId),
        prevUnit: data.prevUnit,
        nextUnit: data.nextUnit,
        course: {
          code: data.courseCode,
          name: data.courseName,
        },
        level: {
          code: data.levelCode,
          name: data.level,
        },
        date: moment(data.date, 'YYYYMMDD'),
        period: data.period as periodType,
        unit: data.unit,
        booth: {
          id: data.boothId,
          building: data.boothBuilding,
          room: data.boothRoom,
          number: data.boothNumber,
        },
        campus: getters.findCampusByCode(data.campusCode),
        taskType: data.taskType as classRecordTaskType,
        students: _.map(data.students, (s): StudentWithGrade => {
          const publics = _.map(s.publicGrades, (g): Grade => ({
            criteria: _.map(g.criteria, (c): Criterion => ({
              id: c.id,
              name: c.name,
              selectedRatingId: c.selectedRatingId,
              ratings: _.map(c.ratings, (rs: any): Rating => ({
                id: rs.id,
                point: rs.point,
                description: rs.description,
                note: rs.note,
              })),
            })),
            comment: g.comment,
            note: g.note,
          }))

          const forEditSource: any = (
            !s.temporaryGrade.isTemporary
            && !s.temporaryGrade.isWaitingApproved
            && publics.length > 0
          )
            // 一時保存データがない、かつ承認待ちデータがない、かつ公開データがある場合は最新の公開データ
            ? _.first(publics)
            // 一時保存データまたは初期値(temporaryGradeに設定される)
            : s.temporaryGrade

          return {
            student: getters.findStudentById(s.studentId),
            wetec: {
              first: s.wetec?.section1st,
              second: s.wetec?.section2nd,
              third: s.wetec?.section3rd,
              fourth: s.wetec?.section4th,
              fifth: s.wetec?.section5th,
              sixth: s.wetec?.section6th,
              total: s.wetec?.totalScore,
            },
            isLocked: s.isLocked,
            applicantId: s.applicantId,
            isApplication: s.isApplication,
            isTemporary: s.temporaryGrade.isTemporary,
            isWaitingApproved: s.temporaryGrade.isWaitingApproved,
            forEdit: {
              criteria: _.map(forEditSource.criteria, (c: any): Criterion => ({
                id: c.id,
                name: c.name,
                selectedRatingId: c.selectedRatingId,
                ratings: _.map(c.ratings, (rs: any): Rating => ({
                  id: rs.id,
                  point: rs.point,
                  description: rs.description,
                  note: rs.note,
                })),
              })),
              comment: forEditSource.comment,
              note: forEditSource.note,
            },
            publics,
          }
        }),
      }

      commit('godClassRecord', classRecord)
    }),

  /**
   * 履修者のクラスレコード情報を一時保存
   * @param {ClassRecordForRegister}
   */
  temporarilySaveStudentClassRecord: (
    context,
    {
      studentId,
      lessonId,
      criteria,
      note,
      comment,
    }: ClassRecordForRegister,
  ) => axios.post(`/lessons/${lessonId}/students/${studentId}/grade/temporary`, {
    criteria: _.map(criteria, ({ id, selectedRating }) => ({
      id,
      selectedRatingId: selectedRating.id,
      point: selectedRating.point,
      name: selectedRating.description,
    })),
    note,
    comment,
  }),

  /**
   * グループ全体のクラスレコード情報を登録
   * @param {lessonId: string, studentGrades: array, comment: string}
   */
  registerGroupClassRecord: (
    context,
    {
      lessonId,
      studentGrades,
      comment,
    }: {
      lessonId: string;
      studentGrades: Array<{
        studentId: string;
        criteria: Array<{
          id: string;
          name: string;
          selectedRatingId: string;
          point: number;
          description: string;
        }>;
        note?: string;
      }>;
      comment?: string;
    },
  ) => axios.post(`/lessons/${lessonId}/grade`, {
    studentGrades,
    comment,
  }),

};

export default actions;
